import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Alert,
  AlertTitle,
  Chip
} from '@mui/material';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import WebIcon from '@mui/icons-material/Web';
import MobileScreenShareIcon from '@mui/icons-material/MobileScreenShare';

interface PrerequisitesSetupComponentProps {
  onBack: () => void;
}

const PrerequisitesSetupComponent: React.FC<PrerequisitesSetupComponentProps> = ({ onBack }) => {
  const [selectedSetup, setSelectedSetup] = useState<string | null>(null);

  const setupScenarios = [
    {
      id: "web-app-testing-setup",
      title: "Configure Web Apps for Token Testing",
      description: "⚠️ REQUIRED: Set up proper platform configuration to enable authentication testing for web applications",
      icon: <WebIcon color="error" />,
      complexity: "Essential"
    },
    {
      id: "spa-testing-setup", 
      title: "Configure SPAs for Token Testing",
      description: "Set up Single Page Applications with correct redirect URIs and CORS settings for AppConfig² testing",
      icon: <MobileScreenShareIcon color="primary" />,
      complexity: "Essential"
    }
  ];

  const renderSetupContent = () => {
    if (selectedSetup === "web-app-testing-setup") {
      return (
        <Box>
          <Typography variant="h5" gutterBottom color="primary">
            Configure Web App for Token Testing
          </Typography>
          
          <Alert severity="warning" sx={{ mb: 3 }}>
            <AlertTitle>Critical Configuration Required</AlertTitle>
            <Typography variant="body2">
              <strong>As opposed to SPA platform, traditional web applications cannot be tested in AppConfig² without proper platform configuration.</strong> 
              This scenario shows you how to temporarily add SPA platform settings to enable authentication testing, 
              then restore your original configuration when testing is complete.
            </Typography>
          </Alert>
          
          <Typography variant="body1" paragraph>
            AppConfig² uses popup-based authentication to test your applications, but traditional web applications 
            are configured for server-side redirect flows. To test these applications, you need to temporarily 
            add Single Page Application (SPA) platform configuration to enable the popup flow without affecting 
            your production web app configuration.
          </Typography>

          <Box sx={{ mb: 3, p: 2, bgcolor: '#fff3e0', borderLeft: '4px solid #ff9800', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ color: '#e65100', mb: 1 }}>
              📋 Before You Start
            </Typography>
            <Typography variant="body2">
              • <strong>Identify your web app</strong> - Look for applications with "Web" platform but no "Single-page application" platform<br/>
              • <strong>Automatic backup created</strong> - AppConfig² automatically creates a "silent backup" when you open an app in the App Tester<br/>
              • <strong>Manual backup</strong> - While you can rely on AppConfig²'s silent backup, it is still good practice to create manual backup before changing app's configuration<br/>
              • <strong>Easy cleanup available</strong> - Use the Backup & Restore feature to restore your original configuration after testing<br/>
              • <strong>Note your current redirect URIs</strong> - You'll need AppConfig²'s redirect URI in addition to existing ones<br/>
              • <strong>Plan for cleanup</strong> - These changes should be reverted after testing to maintain configuration clarity
            </Typography>
          </Box>
          
          <Stepper orientation="vertical">
            <Step active>
              <StepLabel>
                <Typography variant="subtitle1">Add Single Page Application Platform</Typography>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" paragraph>
                  Add SPA platform configuration to enable popup authentication:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="In the App Tester sidebar, navigate to 'Authentication'" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Scroll down to the 'Platform configurations' section" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Click '+ Add a platform'" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Select 'Single-page application'" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Click 'Configure'" />
                  </ListItem>
                </List>
                <Box sx={{ mb: 2 }}>
                  <Button variant="contained" size="small">Continue</Button>
                </Box>
              </StepContent>
            </Step>
            
            <Step active>
              <StepLabel>
                <Typography variant="subtitle1">Configure SPA Redirect URI</Typography>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" paragraph>
                  Add the correct redirect URI for AppConfig² testing:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="In the SPA platform configuration, add the redirect URI:" 
                      secondary={
                        <Box component="span" sx={{ display: 'block', mt: 1, fontFamily: 'monospace', bgcolor: '#f5f5f5', p: 1, borderRadius: 1 }}>
                          {window.location.origin}
                        </Box>
                      }
                    />
                  </ListItem>
                  <ListItem> 
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Click 'Save' to save the SPA platform configuration" />
                  </ListItem>
                </List>
                
                <Box sx={{ my: 2, p: 2, bgcolor: '#e6f4ea', borderRadius: 1, border: '1px solid #acebc0' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Important Notes
                  </Typography>
                  <Typography variant="body2">
                    • <strong>Keep existing web platform:</strong> Do not remove your existing web platform configuration<br/>
                    • <strong>Temporary addition:</strong> This SPA platform is only needed for testing in AppConfig²<br/>
                    • <strong>URI format:</strong> The redirect URI must exactly match your AppConfig² domain<br/>
                    • <strong>Token settings:</strong> SPA platform automatically handles proper token configuration for authorization code flow with PKCE
                  </Typography>
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  <Button variant="contained" size="small">Continue</Button>
                </Box>
              </StepContent>
            </Step>

            <Step active>
              <StepLabel>
                <Typography variant="subtitle1">Apply Configuration Changes</Typography>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" paragraph>
                  Save all platform configuration changes:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Click 'Apply Changes' in the App Tester" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Wait for the confirmation message" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Verify that both 'Web' and 'Single-page application' platforms are now listed" />
                  </ListItem>
                </List>
                <Box sx={{ mb: 2 }}>
                  <Button variant="contained" size="small">Continue</Button>
                </Box>
              </StepContent>
            </Step>
            
            <Step active>
              <StepLabel>
                <Typography variant="subtitle1">Test Authentication</Typography>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" paragraph>
                  Now you can test authentication for your web application:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Navigate to 'Fetch Access Token' in the App Tester sidebar" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Select your desired API audience (Microsoft Graph or custom API)" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Click 'Start Authentication'" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Complete the authentication flow in the popup window" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="View the decoded tokens in the 'Active Tokens' panel" />
                  </ListItem>
                </List>
                <Box sx={{ mb: 2 }}>
                  <Button variant="contained" size="small">Continue</Button>
                </Box>
              </StepContent>
            </Step>

            <Step active>
              <StepLabel>
                <Typography variant="subtitle1">Clean Up After Testing (Recommended)</Typography>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" paragraph>
                  After completing your testing, restore your original configuration using the automatic backup:
                </Typography>
                
                <Box sx={{ mb: 2, p: 2, bgcolor: '#e3f2fd', borderRadius: 1, border: '1px solid #90caf9' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    🎯 Option 1: Use Silent Backup (Recommended)
                  </Typography>
                  <Typography variant="body2">
                    AppConfig² automatically created a backup when you opened this app in the App Tester. 
                    This is the easiest way to restore your original configuration.
                  </Typography>
                </Box>
                
                <List dense>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Navigate to 'Backup & Restore' in the App Tester sidebar" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Expand the 'Browser Storage Management' section" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Click 'Download Latest Backup' to get your original configuration" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Go back to the 'Manual Backup & Restore' section" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Click 'Restore from File' and select the backup file you just downloaded" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Click 'Apply Changes' to restore your original configuration" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Verify that the SPA platform has been removed and only the original Web platform remains" />
                  </ListItem>
                </List>

                <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: '#fff3e0', borderRadius: 1, border: '1px solid #ffcc80' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    🔧 Option 2: Manual Cleanup
                  </Typography>
                  <Typography variant="body2">
                    If you prefer to manually remove the SPA platform:
                  </Typography>
                  <List dense sx={{ mt: 1 }}>
                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                      <ListItemText primary="Return to 'Authentication' section in App Tester" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                      <ListItemText primary="Find the 'Single-page application' platform and click delete/remove" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                      <ListItemText primary="Click 'Apply Changes' to save the updated configuration" />
                    </ListItem>
                  </List>
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  <Button variant="contained" size="small">Finish</Button>
                </Box>
              </StepContent>
            </Step>
          </Stepper>
          
          <Box sx={{ mt: 4, p: 2, bgcolor: '#f0f7ff', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Key Technical Details
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="Authorization Code Flow with PKCE" 
                  secondary="SPA platform uses the secure authorization code flow with PKCE, not implicit flow" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="Multiple Platform Support" 
                  secondary="Applications can have both Web and SPA platforms configured simultaneously" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="Popup vs Redirect Flow" 
                  secondary="SPA platform enables popup authentication required by AppConfig² testing interface" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="Production Impact" 
                  secondary="Adding SPA platform for testing doesn't affect your production web application flows" 
                />
              </ListItem>
            </List>
          </Box>
        </Box>
      );
    }

    if (selectedSetup === "spa-testing-setup") {
      return (
        <Box>
          <Typography variant="h5" gutterBottom color="primary">
            Configure SPA for Token Testing
          </Typography>
          
          <Alert severity="info" sx={{ mb: 3 }}>
            <AlertTitle>SPA Configuration Essentials</AlertTitle>
            <Typography variant="body2">
              Single Page Applications (SPAs) require specific configuration to work properly with AppConfig²'s 
              testing interface. This scenario ensures your SPA is configured with the correct redirect URIs, 
              CORS settings, and token handling for successful authentication testing.
            </Typography>
          </Alert>
          
          <Typography variant="body1" paragraph>
            SPAs use the Authorization Code Flow with PKCE and rely on popup-based authentication for testing. 
            Unlike web applications, SPAs are naturally compatible with AppConfig²'s testing approach, but they 
            need proper redirect URI configuration and CORS settings to work correctly.
          </Typography>

          <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f9ff', borderLeft: '4px solid #0078d4', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ color: '#0078d4', mb: 1 }}>
              📋 Before You Start
            </Typography>
            <Typography variant="body2">
              • <strong>Identify your SPA</strong> - Look for applications with "Single-page application" platform<br/>
              • <strong>Automatic backup created</strong> - AppConfig² automatically creates a "silent backup" when you open an app in the App Tester<br/>
              • <strong>Check current redirect URIs</strong> - Note any existing redirect URIs for your production SPA<br/>
              • <strong>CORS considerations</strong> - Ensure your SPA can handle popup authentication flows<br/>
              • <strong>Token configuration</strong> - Verify implicit grant settings are properly configured
            </Typography>
          </Box>
          
          <Stepper orientation="vertical">
            <Step active>
              <StepLabel>
                <Typography variant="subtitle1">Verify SPA Platform Configuration</Typography>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" paragraph>
                  First, ensure your application is properly configured as a Single Page Application:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="In the App Tester sidebar, navigate to 'Authentication'" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Scroll down to the 'Platform configurations' section" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Confirm that 'Single-page application' platform is present" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="If not present, click '+ Add a platform' → 'Single-page application'" />
                  </ListItem>
                </List>
                <Box sx={{ mb: 2 }}>
                  <Button variant="contained" size="small">Continue</Button>
                </Box>
              </StepContent>
            </Step>
            
            <Step active>
              <StepLabel>
                <Typography variant="subtitle1">Configure Redirect URI for AppConfig²</Typography>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" paragraph>
                  Add the AppConfig² redirect URI to your SPA platform configuration:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Click on the 'Single-page application' platform to expand it" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="In the 'Redirect URIs' section, add:" 
                      secondary={
                        <Box component="span" sx={{ display: 'block', mt: 1, fontFamily: 'monospace', bgcolor: '#f5f5f5', p: 1, borderRadius: 1 }}>
                          {window.location.origin}
                        </Box>
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Keep any existing redirect URIs for your production SPA" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Click 'Save' to save the platform configuration" />
                  </ListItem>
                </List>
                
                <Box sx={{ my: 2, p: 2, bgcolor: '#e6f4ea', borderRadius: 1, border: '1px solid #acebc0' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Multiple Redirect URIs
                  </Typography>
                  <Typography variant="body2">
                    • <strong>Production URI:</strong> Keep your existing redirect URIs for production use<br/>
                    • <strong>Testing URI:</strong> Add AppConfig²'s URI for testing purposes<br/>
                    • <strong>Development URIs:</strong> You can have multiple URIs for different environments<br/>
                    • <strong>Exact match required:</strong> Redirect URIs must match exactly (including https/http)
                  </Typography>
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  <Button variant="contained" size="small">Continue</Button>
                </Box>
              </StepContent>
            </Step>

            <Step active>
              <StepLabel>
                <Typography variant="subtitle1">Configure Token Settings</Typography>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" paragraph>
                  Ensure proper token configuration for your SPA:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Still in the Authentication section, scroll to 'Implicit grant and hybrid flows'" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="For modern SPAs, ensure 'Access tokens' is checked" 
                      secondary="This allows your SPA to receive access tokens directly"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Optionally check 'ID tokens' if your SPA needs ID tokens" 
                      secondary="Required for user identity information"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Note: Authorization Code Flow with PKCE is still the primary flow" 
                      secondary="These settings enable additional token types as needed"
                    />
                  </ListItem>
                </List>
                
                <Box sx={{ my: 2, p: 2, bgcolor: '#fff8e6', borderRadius: 1, border: '1px solid #ffd87c' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Token Flow Security
                  </Typography>
                  <Typography variant="body2">
                    Modern SPAs should primarily use Authorization Code Flow with PKCE. The implicit grant 
                    settings above are supplementary and allow the SPA to receive tokens directly when needed 
                    for testing or specific use cases.
                  </Typography>
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  <Button variant="contained" size="small">Continue</Button>
                </Box>
              </StepContent>
            </Step>

            <Step active>
              <StepLabel>
                <Typography variant="subtitle1">Apply Configuration Changes</Typography>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" paragraph>
                  Save all configuration changes:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Click 'Apply Changes' in the App Tester" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Wait for the confirmation message" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Verify the redirect URI has been added successfully" />
                  </ListItem>
                </List>
                <Box sx={{ mb: 2 }}>
                  <Button variant="contained" size="small">Continue</Button>
                </Box>
              </StepContent>
            </Step>
            
            <Step active>
              <StepLabel>
                <Typography variant="subtitle1">Test SPA Authentication</Typography>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" paragraph>
                  Now test the authentication flow for your SPA:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Navigate to 'Fetch Access Token' in the App Tester sidebar" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Select your desired API audience (Microsoft Graph or custom API)" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Choose appropriate scopes for your testing scenario" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Click 'Start Authentication'" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Complete the authentication flow in the popup window" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Verify tokens appear in the 'Active Tokens' panel" />
                  </ListItem>
                </List>
                
                <Box sx={{ my: 2, p: 2, bgcolor: '#e6f4ea', borderRadius: 1, border: '1px solid #acebc0' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Expected Behavior
                  </Typography>
                  <Typography variant="body2">
                    • <strong>Popup opens:</strong> Authentication should occur in a popup window<br/>
                    • <strong>Redirect works:</strong> After login, popup should redirect to AppConfig²<br/>
                    • <strong>Tokens received:</strong> Both access and ID tokens should be available<br/>
                    • <strong>Popup closes:</strong> Authentication popup should close automatically
                  </Typography>
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  <Button variant="contained" size="small">Continue</Button>
                </Box>
              </StepContent>
            </Step>

            <Step active>
              <StepLabel>
                <Typography variant="subtitle1">Verify Token Contents</Typography>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" paragraph>
                  Examine the tokens to ensure they contain the expected information:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="In the 'Active Tokens' panel, expand the token details" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Check the access token's 'aud' (audience) claim" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Verify the 'scp' (scopes) claim contains requested scopes" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="If you requested an ID token, check user identity claims" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Note token expiration times and refresh token availability" />
                  </ListItem>
                </List>
                
                <Box sx={{ my: 2, p: 2, bgcolor: '#e3f2fd', borderRadius: 1, border: '1px solid #90caf9' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Token Validation Tips
                  </Typography>
                  <Typography variant="body2">
                    • <strong>Copy tokens:</strong> Use the copy button to get raw tokens for external validation<br/>
                    • <strong>JWT.io validation:</strong> Paste tokens into jwt.io to verify structure and claims<br/>
                    • <strong>Expiration check:</strong> Ensure tokens have reasonable expiration times<br/>
                    • <strong>Scope validation:</strong> Confirm requested scopes match received scopes
                  </Typography>
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  <Button variant="contained" size="small">Finish</Button>
                </Box>
              </StepContent>
            </Step>
          </Stepper>
          
          <Box sx={{ mt: 4, p: 2, bgcolor: '#f0f7ff', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              SPA Testing Best Practices
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="Use Authorization Code Flow with PKCE" 
                  secondary="This is the recommended and secure flow for SPAs, preferred over implicit flow" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="Configure Multiple Redirect URIs" 
                  secondary="Include URIs for development, testing, and production environments" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="Test with Different Scopes" 
                  secondary="Verify your SPA can request and receive appropriate scopes for different scenarios" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="Handle Token Refresh" 
                  secondary="Test token refresh scenarios to ensure your SPA can maintain user sessions" 
                />
              </ListItem>
            </List>
          </Box>
          
          <Box sx={{ mt: 4, p: 2, bgcolor: '#fff3e0', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Common SPA Configuration Issues
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="warning" /></ListItemIcon>
                <ListItemText 
                  primary="AADSTS50011: Redirect URI mismatch" 
                  secondary="Ensure redirect URIs exactly match, including protocol (https vs http)" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="warning" /></ListItemIcon>
                <ListItemText 
                  primary="Popup blocked by browser" 
                  secondary="Ensure popup blocking is disabled for the AppConfig² domain" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="warning" /></ListItemIcon>
                <ListItemText 
                  primary="CORS errors during authentication" 
                  secondary="SPA platform should handle CORS automatically; check browser developer tools" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="warning" /></ListItemIcon>
                <ListItemText 
                  primary="Token not received" 
                  secondary="Verify implicit grant settings are enabled if using implicit flow" 
                />
              </ListItem>
            </List>
          </Box>
        </Box>
      );
    }

    return null;
  };

  if (selectedSetup) {
    return (
      <Box>
        {renderSetupContent()}
        <Box sx={{ mt: 4 }}>
          <Button 
            variant="outlined" 
            startIcon={<ArrowRightIcon />}
            onClick={() => setSelectedSetup(null)}
          >
            Back to Prerequisites & Setup
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom color="primary">
        Prerequisites & Setup
      </Typography>
      
      <Typography variant="body1" paragraph>
        Before testing your applications with AppConfig², you need to ensure they're properly configured for 
        authentication testing. These essential setup scenarios guide you through the required platform 
        configurations for different application types.
      </Typography>
      
      <Alert severity="error" sx={{ mb: 3 }}>
        <AlertTitle>Required Setup</AlertTitle>
        <Typography variant="body2">
          <strong>These configurations are essential for authentication testing.</strong> Without proper setup, 
          your applications will not work correctly with AppConfig²'s testing interface. Complete the appropriate 
          setup scenario for your application type before proceeding with other test scenarios.
        </Typography>
      </Alert>
      
      <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2 }}>
        Choose Your Application Type
      </Typography>
      
      <Grid container spacing={3}>
        {setupScenarios.map((scenario) => (
          <Grid item xs={12} md={6} key={scenario.id}>
            <Card 
              variant="outlined"
              sx={{ 
                cursor: 'pointer',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                border: scenario.complexity === 'Essential' ? '2px solid #f44336' : '1px solid #e0e0e0',
                '&:hover': {
                  boxShadow: 3,
                  transform: 'translateY(-2px)',
                  transition: 'all 0.2s'
                }
              }}
              onClick={() => setSelectedSetup(scenario.id)}
            >
              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {scenario.icon}
                  <Typography variant="h6" sx={{ ml: 1, flexGrow: 1 }}>
                    {scenario.title}
                  </Typography>
                  <Chip 
                    label={scenario.complexity} 
                    size="small" 
                    color="error"
                    variant="outlined"
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                  {scenario.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 4, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
        <Typography variant="subtitle2" gutterBottom>
          Why These Setups Are Required
        </Typography>
        <List dense>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="info" /></ListItemIcon>
            <ListItemText 
              primary="AppConfig² uses popup-based authentication" 
              secondary="Traditional web apps need SPA platform for popup flow compatibility"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="info" /></ListItemIcon>
            <ListItemText 
              primary="Redirect URI configuration is critical" 
              secondary="Apps must include AppConfig²'s domain in their redirect URI list"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="info" /></ListItemIcon>
            <ListItemText 
              primary="Token settings must be properly configured" 
              secondary="Implicit grant and PKCE settings affect token flow behavior"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="info" /></ListItemIcon>
            <ListItemText 
              primary="Cleanup guidance is provided" 
              secondary="Instructions for reverting testing configurations back to production settings"
            />
          </ListItem>
        </List>
      </Box>
      
      <Box sx={{ mt: 4 }}>
        <Button 
          variant="outlined" 
          startIcon={<ArrowRightIcon />}
          onClick={onBack}
        >
          Back to All Scenarios
        </Button>
      </Box>
    </Box>
  );
};

export default PrerequisitesSetupComponent;